import { get, post, put, del } from '@/lib/api/client'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Tender,
  TenderStats,
  CreateTender,
  UpdateTender,
  TenderFilterParams
} from '@/lib/api/types'

export const tenderService = {
  // Filter tenders with pagination
  filterTenders: async (
    params: PaginationParams = { page: 1, size: 10 },
    filters: TenderFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Tender>>> => {
    return await post<ApiResponse<PaginatedResponse<Tender>>>(
      `tenders/private/filter?size=${params.size}&page=${params.page}`,
      filters
    )
  },

  // Get tender by ID
  getTenderById: async (tenderId: string): Promise<ApiResponse<Tender>> => {
    return await get<ApiResponse<Tender>>(`tenders/private/${tenderId}`)
  },

  // Create a new tender
  createTender: async (data: CreateTender): Promise<ApiResponse<Tender>> => {
    return await post<ApiResponse<Tender>>(
      'tenders/admin/private',
      data
    )
  },

  // Update an existing tender
  updateTender: async (
    tenderId: string,
    data: UpdateTender
  ): Promise<ApiResponse<Tender>> => {
    return await put<ApiResponse<Tender>>(
      `tenders/admin/private/${tenderId}`,
      data
    )
  },

  // Delete a tender
  deleteTender: async (tenderId: string): Promise<ApiResponse<null>> => {
    return await del<ApiResponse<null>>(
      `tenders/admin/private/${tenderId}`
    )
  },

  // Get draft tenders count
  getDraftTendersCount: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>('tenders/private/stats/draft')
  },

  // Get published tenders count
  getPublishedTendersCount: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>('tenders/private/stats/published')
  },

  // Search tenders
  searchTenders: async (
    searchBy?: string
  ): Promise<ApiResponse<Tender[]>> => {
    const queryParams = new URLSearchParams()

    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<Tender[]>>(
      `tenders/private/search?${queryParams.toString()}`
    )
  },

  // Get bid types
  getBidTypes: async (): Promise<ApiResponse<Array<{ value: string; displayName: string }>>> => {
    return await get<ApiResponse<Array<{ value: string; displayName: string }>>>(
      'tenders/private/bid-types'
    )
  },

  // Get tender categories
  getTenderCategories: async (): Promise<ApiResponse<Array<{ value: string; displayName: string }>>> => {
    return await get<ApiResponse<Array<{ value: string; displayName: string }>>>(
      'tenders/private/tender-category'
    )
  },

  // Get tender stats
  getTenderStats: async (): Promise<ApiResponse<TenderStats>> => {
    return await get<ApiResponse<TenderStats>>('tenders/private/stats')
  }
}
